const _ = require('lodash');

const { cloneDeep, find, get, includes, max } = _;

const factory = ($q, Model, PubnubActions, PriorityService) => {
  class DashboardClient extends Model {
    // override to prevent fetch on instantiation
    $init() {
      this.$subscribe(PubnubActions.messageCreated({ clientId: this.id }), this.$onMessage);
    }

    $onMessage(e) {
      const authorRoles = e.context.eventSource.authorRoles;
      const createdAt = e.createdAt;

      if (authorRoles.indexOf('USER') > -1) {
        this.$set({
          more: Object.assign({}, this.more, {
            lastContact: new Date(createdAt),
          }),
        });
      }
    }

    $serialize(data) {
      const { lastContact, unresponsiveDate } = data.more;
      const serializedData = Object.assign({}, data);

      serializedData.more.lastContact = lastContact ? new Date(lastContact) : null;
      serializedData.more.unresponsiveDate = unresponsiveDate ? new Date(unresponsiveDate) : null;
      return serializedData;
    }

    /**
     * This shis should be refactored on the backend to be one endpoint
     */
    $fetch(shouldSet = true) {
      this.$lastFetched = null;
      return $q.all([super.$fetch({}, shouldSet), this.$$fetchAccounts()]).then(res => {
        if (shouldSet) {
          return (this.$lastFetched = new Date());
        }
        return res[0];
      });
    }

    $$fetchAccounts() {
      return this.$apiCall({
        method: 'GET',
        url: `dashboard.json/accounts/client/${this.$storeId()}`,
      }).then(res => this.$set({ accounts: res.data }));
    }

    $getMitoOutstanding() {
      return this.$apiCall({
        method: 'GET',
        url: `dashboard.json/mito-transactions/client/${this.$storeId()}`,
      }).then(res => res.data);
    }

    $getUnclassifiedPbTransactions() {
      return this.$apiCall({
        method: 'GET',
        url: `dashboard.json/unclassified-pb-transactions/client/${this.$storeId()}`,
      }).then(res => res.data);
    }

    $getAwaitingCategoryTransactions() {
      return this.$apiCall({
        method: 'GET',
        url: `dashboard.json/awaiting-categorization-transactions/client/${this.$storeId()}?detailed=true`,
      }).then(res => res.data);
    }

    $getOptimizedAccounts() {
      return this.accounts.filter(account => account.optimizationStatus === 'Optimized');
    }

    $getLastStatement() {
      this.$lastStatement = max(this.accounts.map(account => account.statementDayOfMonth)) || 0;

      return this.$lastStatement;
    }

    getInboxStateLabel() {
      const state = find(DashboardClient.inboxStateOptions, { value: this.inboxState });
      return state ? state.name : '';
    }

    getPrincipalPersonLastLoginTimeForDisplay() {
      return this.principalPersonLastLoginTime
        ? new Date(this.principalPersonLastLoginTime).toLocaleString()
        : '';
    }

    $getMonthlyStateMap(statusType) {
      return _(this.more.monthlyData)
        .filter(item => item[statusType])
        .keyBy('month')
        .value();
    }

    $$getMonthlyData() {
      return get(this, 'more.monthlyData') || [];
    }

    $$findMonthlyDataItem(yearMonth) {
      return find(this.$$getMonthlyData(), {
        month: yearMonth,
      });
    }

    $$setMonthlyState(state) {
      const monthlyData = cloneDeep(this.more.monthlyData);
      const existingState = this.$$findMonthlyState(state.month, monthlyData);

      if (existingState) {
        monthlyData[monthlyData.indexOf(existingState)] = state;
      } else {
        monthlyData.push(state);
      }

      this.$set({
        more: Object.assign({}, this.more, { monthlyData }),
      });

      return this.$updateGreenAndRedStatusCountAndReviewedMonth();
    }

    $$findMonthlyState(month, monthlyData) {
      return find(monthlyData || this.more.monthlyData, { month });
    }

    $$getMonthlyState(month, type, status) {
      const state = this.$$findMonthlyState(month) || {
        month,
        accountId: this.id,
      };

      state[type] = status;

      return state;
    }

    $saveMonthlyState(month, type, status) {
      const data = this.$$getMonthlyState(month, type, status);

      return this.$apiCall({
        method: 'PUT',
        url: `dashboard.json/monthly/client/${this.$storeId()}`,
        data,
      }).then(() => this.$$setMonthlyState(data));
    }

    $getReviewedMonth() {
      if (!this.mostRecentReviewedMonth) {
        return undefined;
      }
      const dateStringArr = this.mostRecentReviewedMonth.split('-');
      return new Date(parseInt(dateStringArr[0], 10), dateStringArr[1] - 1, 1);
    }

    $put(data) {
      return super.$put(data, {}, { fetch: false });
    }

    $$activeAt(account, yearMonth) {
      const lowerBound = account.dateActive || '1970-01';
      const upperBound = account.dateInactive || '2099-12';
      return lowerBound <= yearMonth && yearMonth <= upperBound;
    }

    /**
     * Gets the red  and green status count, and earliest reviewed month from the server
     */
    $updateGreenAndRedStatusCountAndReviewedMonth() {
      return this.$apiCall({
        method: 'GET',
        url: `dashboard.json/client/${this.$storeId()}`,
      }).then(({ data }) =>
        this.$set({
          monthlyGreenCount: data.greenCount || 0,
          monthlyRedCount: data.redCount || 0,
          yearlyGreenCount: data.yearlyGreenCount || 0,
          yearlyRedCount: data.yearlyRedCount || 0,
          mostRecentReviewedMonth: data.mostRecentReviewedMonth,
          principalPersonLastLoginTime: data.principalPersonLastLoginTime,
        }),
      );
    }

    /**
     * Sets a collection of events to the client model for sorting operations at
     * the client collection level
     */
    $setOldestEventTimes(eventTimes) {
      this.$oldestEventTimes = eventTimes;
    }

    /**
     * Updates the client's priority value
     */
    $updatePriority(currentTime) {
      const times = {
        ...this.$oldestEventTimes,
        ...this.oldestGreenStateTimes,
        lastContact: this.more.lastContact,
      };
      this.$priority = PriorityService.getScore(times, currentTime);
    }

    /**
     * syncs account data stored in the client model
     * @param {object} newAccountData - new account data to be set
     */
    $syncAccountStatus(newAccountData) {
      const accounts = cloneDeep(this.accounts);
      const account = find(accounts, { id: newAccountData.id });

      if (account) {
        Object.assign(account, newAccountData);
        this.$set({ accounts });
        this.$updateGreenAndRedStatusCountAndReviewedMonth();
      }
    }

    /**
     * check whether a client is actively undergoing bookkeeping
     */
    $isActive(bookkeeperId) {
      return (
        this.$isPrimaryAssignedPerson(bookkeeperId) &&
        this.inboxState === 'KEEP_UP' &&
        includes(['HEALTHY', 'UNRESPONSIVE'], this.clientCareState)
      );
    }

    $isPinned(pinnedClientIds) {
      return pinnedClientIds.has(this.id);
    }

    $isPrimaryAssignedPerson(bookkeeperId) {
      return bookkeeperId === this.$$getPrimaryAssignedPerson();
    }

    $isSecondaryAssignedPerson(bookkeeperId) {
      return includes(this.$$getSecondaryAssignedPersons(), bookkeeperId);
    }

    $$getPrimaryAssignedPerson() {
      return get(this.assignedPersons, 'bookkeepers.primary.id');
    }

    $$getSecondaryAssignedPersons() {
      return [
        'accountants.primary.id',
        'bookkeepers.secondary.id',
        'bookkeepers.tertiary.id',
        'bookkeepers.fourth.id',
        'bookkeepers.fifth.id',
        'bookkeepers.sixth.id',
        'salespersons.primary.id',
        'salespersons.secondary.id',
        'salespersons.tertiary.id',
        'salespersons.fourth.id',
        'salespersons.fifth.id',
        'clientSuccess.primary.id',
        'clientSuccess.secondary.id',
        'clientSuccess.tertiary.id',
        'clientSuccess.fourth.id',
        'clientSuccess.fifth.id',
        'retro.primary.id',
        'retro.secondary.id',
        'retro.tertiary.id',
      ].map(x => get(this.assignedPersons, x));
    }
  }

  Object.assign(DashboardClient.prototype, {
    $resource: 'client',
    accounts: [],
    $oldestEventTimes: [],
    $priority: 0,
    $lastFetched: 0,
    $tasksCount: 0,
  });

  DashboardClient.inboxStateOptions = [
    { name: 'Keep Up', value: 'KEEP_UP' },
    { name: 'Catch Up', value: 'CATCH_UP' },
    { name: 'Demo', value: 'DEMO' },
    { name: 'Prospective', value: 'PROSPECTIVE' },
    { name: 'Churn', value: 'CHURN' },
    { name: 'Waitlist', value: 'WAITLIST' },
    { name: 'Testing', value: 'TESTING' },
    { name: 'Lost Trial', value: 'LOST_TRIAL' },
    { name: 'Tr - Setup', value: 'TRIAL_QUALIFY' },
    { name: 'Tr - Working', value: 'TRIAL_EXECUTE' },
    { name: 'Tr - Call', value: 'TRIAL_REVIEW' },
    { name: 'Tr - Convert', value: 'TRIAL_CONVERT' },
  ];

  DashboardClient.clientCareStateLabels = [
    {
      value: 'HEALTHY',
      name: 'Healthy',
    },
    {
      value: 'UNRESPONSIVE',
      name: 'Unresponsive',
    },
    {
      value: 'DELINQUENT',
      name: 'Delinquent',
    },
    {
      value: 'ON_HOLD',
      name: 'On Hold',
    },
  ];

  DashboardClient.labelOptions = [
    {
      name: '',
      value: null,
    },
    {
      name: '✉️',
      value: 'MAIL',
    },
    {
      name: '📞',
      value: 'PHONE',
    },
    {
      name: '📅',
      value: 'CALENDAR',
    },
    {
      name: '🖥',
      value: 'COMPUTER',
    },
    {
      name: '🗄',
      value: 'FILE',
    },
    {
      name: '📌',
      value: 'PIN',
    },
    {
      name: '⁉️',
      value: 'QUESTION',
    },
    {
      name: '🎉',
      value: 'TADA',
    },
    {
      name: '⏱',
      value: 'STOPWATCH',
    },
    {
      name: '✈️',
      value: 'PLANE',
    },
    {
      name: '⚙️',
      value: 'GEAR',
    },
    {
      name: '👀',
      value: 'EYES',
    },
    {
      name: '⚡️',
      value: 'LIGHTNING',
    },
    {
      name: '🔥',
      value: 'FIRE',
    },
    {
      name: '❄️',
      value: 'SNOWFLAKE',
    },
    {
      name: '🔙',
      value: 'BACK',
    },
    {
      name: '⚠️',
      value: 'WARNING',
    },
  ];

  return DashboardClient;
};

const moduleDeps = [require('data/util').name, require('data/service/priority').name];

module.exports = angular
  .module('bench.data.model.dashboardClient', moduleDeps)
  .factory('DashboardClient', factory);
